import { useEffect, useState } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  FileText, DollarSign, Clock, CheckCircle, XCircle, 
  Download, CreditCard, Search, Filter, Building2,
  Banknote, Receipt, TrendingUp, AlertCircle, RefreshCw
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY_ID = 'rzp_test_JlxMVLdHwOPLu4';

const SuperAdminBillingPage = () => {
  const { getAuthHeader } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    pendingAmount: 0,
    paidAmount: 0,
    overdueCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Payment Dialog
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentData, setPaymentData] = useState({
    payment_method: 'bank_transfer',
    payment_reference: '',
    notes: ''
  });

  useEffect(() => {
    fetchInvoices();
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/invoices`, {
        headers: getAuthHeader()
      });
      setInvoices(response.data);
      calculateStats(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (invoiceList) => {
    const stats = {
      totalInvoices: invoiceList.length,
      pendingAmount: 0,
      paidAmount: 0,
      overdueCount: 0
    };

    const today = new Date();
    invoiceList.forEach(inv => {
      const amount = inv.total_amount || inv.amount || 0;
      if (inv.status === 'paid') {
        stats.paidAmount += amount;
      } else if (inv.status === 'sent' || inv.status === 'overdue') {
        stats.pendingAmount += amount;
        if (new Date(inv.due_date) < today) {
          stats.overdueCount++;
        }
      }
    });

    setStats(stats);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      sent: { color: 'bg-blue-100 text-blue-800', label: 'Pending' },
      paid: { color: 'bg-green-100 text-green-800', label: 'Paid' },
      overdue: { color: 'bg-red-100 text-red-800', label: 'Overdue' },
      cancelled: { color: 'bg-gray-100 text-gray-800', label: 'Cancelled' }
    };
    const config = statusConfig[status] || statusConfig.sent;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentData({
      payment_method: 'bank_transfer',
      payment_reference: '',
      notes: ''
    });
    setPaymentDialog(true);
  };

  const handleMarkPaid = async () => {
    if (!selectedInvoice) return;
    
    try {
      await axios.patch(
        `${API_URL}/api/invoices/${selectedInvoice.id}/mark-paid`,
        paymentData,
        { headers: getAuthHeader() }
      );
      toast.success('Invoice marked as paid successfully');
      setPaymentDialog(false);
      fetchInvoices();
    } catch (error) {
      console.error('Failed to mark invoice as paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    }
  };

  // Simulate Test Payment (for demo/testing without real Razorpay)
  const handleSimulatePayment = async (invoice) => {
    if (!confirm(`Simulate payment of ₹${invoice.total_amount?.toLocaleString()} for ${invoice.invoice_number}?\n\nThis will mark the invoice as PAID (Test Mode).`)) {
      return;
    }
    
    setProcessingPayment(true);
    
    try {
      await axios.patch(
        `${API_URL}/api/invoices/${invoice.id}/mark-paid`,
        {
          payment_method: 'online',
          payment_reference: `TEST_PAY_${Date.now()}`,
          notes: 'Simulated test payment'
        },
        { headers: getAuthHeader() }
      );
      toast.success('Test payment successful! Invoice marked as paid.');
      fetchInvoices();
    } catch (error) {
      console.error('Simulate payment failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to simulate payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  // Razorpay Online Payment (requires valid API keys)
  const handleOnlinePayment = async (invoice) => {
    // For now, use simulated payment since no valid Razorpay keys
    handleSimulatePayment(invoice);
    return;
    
    /* Real Razorpay implementation - uncomment when API keys are available
    if (!window.Razorpay) {
      toast.error('Payment gateway not loaded. Please refresh the page.');
      return;
    }
    
    setProcessingPayment(true);
    
    try {
      // Create order on backend
      const orderResponse = await axios.post(
        `${API_URL}/api/invoices/${invoice.id}/create-payment-order`,
        {},
        { headers: getAuthHeader() }
      );
      
      const { order_id, amount, currency, invoice_number } = orderResponse.data;
      
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: amount,
        currency: currency,
        name: 'Cloud Campus Schools',
        description: `Payment for Invoice ${invoice_number}`,
        order_id: order_id,
        handler: async (response) => {
          // Verify payment on backend
          try {
            await axios.post(
              `${API_URL}/api/invoices/${invoice.id}/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              { headers: getAuthHeader() }
            );
            toast.success('Payment successful! Invoice marked as paid.');
            fetchInvoices();
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: invoice.school?.name || 'School Admin',
          email: invoice.school?.email || '',
          contact: invoice.school?.phone || ''
        },
        theme: {
          color: '#3b82f6'
        },
        modal: {
          ondismiss: () => {
            setProcessingPayment(false);
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Failed to create payment order:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setProcessingPayment(false);
    }
    */
  };

  const handleGenerateRenewalInvoices = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/invoices/generate-for-expiring`,
        {},
        { headers: getAuthHeader() }
      );
      
      const { invoices_generated, notifications_sent } = response.data;
      
      if (invoices_generated > 0) {
        toast.success(`Generated ${invoices_generated} renewal invoice(s). ${notifications_sent} notification(s) sent.`);
        fetchInvoices();
      } else {
        toast.info('No subscriptions expiring in the next 7 days.');
      }
    } catch (error) {
      console.error('Failed to generate renewal invoices:', error);
      toast.error('Failed to generate renewal invoices');
    }
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    const filename = `Invoice_${invoiceNumber || invoiceId}.pdf`;
    
    try {
      toast.info('Downloading PDF...');
      
      const response = await axios.get(
        `${API_URL}/api/invoices/${invoiceId}/pdf`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Check if response is valid
      if (!response.data || response.data.size === 0) {
        toast.error('Empty PDF response');
        return;
      }
      
      // Use file-saver library - works on all browsers
      const blob = new Blob([response.data], { type: 'application/pdf' });
      saveAs(blob, filename);
      
      toast.success('Invoice downloaded!');
      
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download invoice');
    }
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = 
      inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.school?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.school?.school_code?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <DashboardLayout title="Billing & Invoices">
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Invoices</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalInvoices}</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Amount</CardTitle>
                <Clock className="h-4 w-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">₹{stats.pendingAmount.toLocaleString()}</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Collected Amount</CardTitle>
                <TrendingUp className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">₹{stats.paidAmount.toLocaleString()}</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Overdue</CardTitle>
                <AlertCircle className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{stats.overdueCount}</div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Invoices Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                All Invoices
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  onClick={handleGenerateRenewalInvoices}
                  className="mr-2"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Generate Renewal Invoices
                </Button>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search invoices..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-[250px]"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="sent">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-muted-foreground">Loading invoices...</p>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No invoices found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Invoice #</th>
                      <th className="text-left py-3 px-4 font-medium">Type</th>
                      <th className="text-left py-3 px-4 font-medium">School</th>
                      <th className="text-left py-3 px-4 font-medium">Plan</th>
                      <th className="text-left py-3 px-4 font-medium">Amount</th>
                      <th className="text-left py-3 px-4 font-medium">Due Date</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                      <th className="text-left py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4 font-mono text-sm">{invoice.invoice_number}</td>
                        <td className="py-3 px-4">
                          <Badge variant={invoice.invoice_type === 'renewal' ? 'secondary' : 'outline'}>
                            {invoice.invoice_type === 'renewal' ? 'Renewal' : 'New'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="font-medium">{invoice.school?.name || 'N/A'}</p>
                              <p className="text-xs text-muted-foreground">{invoice.school?.school_code}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">{invoice.plan?.name || invoice.plan_name || 'N/A'}</td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-semibold">₹{(invoice.total_amount || invoice.amount || 0).toLocaleString()}</p>
                            {invoice.tax_amount > 0 && (
                              <p className="text-xs text-muted-foreground">+₹{invoice.tax_amount?.toLocaleString()} GST</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">{invoice.due_date}</td>
                        <td className="py-3 px-4">{getStatusBadge(invoice.status)}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadPDF(invoice.id, invoice.invoice_number)}
                              title="Download PDF"
                              data-testid={`download-invoice-${invoice.id}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                              <>
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={() => handleOnlinePayment(invoice)}
                                  title="Simulate Test Payment"
                                  disabled={processingPayment}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <CreditCard className="h-4 w-4 mr-1" />
                                  Pay (Test)
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openPaymentDialog(invoice)}
                                  title="Record Manual Payment"
                                >
                                  <Banknote className="h-4 w-4 mr-1" />
                                  Manual
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Dialog */}
        <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Record Payment
              </DialogTitle>
            </DialogHeader>
            {selectedInvoice && (
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground">Invoice</p>
                      <p className="font-semibold">{selectedInvoice.invoice_number}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">School</p>
                      <p className="font-semibold">{selectedInvoice.school?.name || selectedInvoice.school_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Amount</p>
                      <p className="font-semibold text-lg">₹{(selectedInvoice.total_amount || selectedInvoice.amount || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Due Date</p>
                      <p className="font-semibold">{selectedInvoice.due_date}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label>Payment Method</Label>
                    <Select 
                      value={paymentData.payment_method} 
                      onValueChange={(val) => setPaymentData({...paymentData, payment_method: val})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Reference Number / Transaction ID</Label>
                    <Input
                      placeholder="e.g., CHQ123456 or TXN987654"
                      value={paymentData.payment_reference}
                      onChange={(e) => setPaymentData({...paymentData, payment_reference: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      placeholder="Any additional notes..."
                      value={paymentData.notes}
                      onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                      rows={2}
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setPaymentDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleMarkPaid}>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Confirm Payment
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminBillingPage;
